import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  IosShare as ShareIcon,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import {
  PRICE_PROMO_CANVAS,
  PRICE_PROMO_TEMPLATES,
  PricePromoField,
  fillPromoTemplate,
} from '../../price-promo-template/pricePromoTemplates';

export type PricePromoValues = Record<PricePromoField, string>;

interface PricePromoShareTabProps {
  /** Field values injected into the chosen design's {{ placeholders }}. */
  values: PricePromoValues;
  /** Base name (without extension) for the downloaded/shared image. */
  fileName: string;
}

/**
 * Grid preview markup: same design + live captions as capture, but swap the
 * full-res background for the small thumb JPEG so six tiles don't OOM Safari.
 * Also drop backdrop-filter (expensive on iOS, negligible at thumbnail size).
 */
function fillPromoTemplatePreview(
  markup: string,
  values: PricePromoValues,
  templateId: string,
): string {
  return fillPromoTemplate(markup, values)
    .replace(`/price-promo/${templateId}-bg.jpg`, `/price-promo/${templateId}-thumb.jpg`)
    .replace(`/price-promo/${templateId}-bg.png`, `/price-promo/${templateId}-thumb.jpg`)
    .replace(/backdrop-filter:[^;"]*;?/gi, '');
}

/** Wait for every <img> under el to finish loading (resolves even on error). */
function waitForImages(el: HTMLElement): Promise<void> {
  const imgs = Array.from(el.querySelectorAll('img'));
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  ).then(() => undefined);
}

/**
 * iOS Safari (and other low-memory mobile browsers) often jetsam the tab when
 * html2canvas allocates a 2× 1080 canvas + a base64 data URL. Prefer scale 1
 * on coarse-pointer / iOS devices — 1080² is already social-share resolution.
 */
function captureScale(): number {
  if (typeof window === 'undefined') return 1;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const coarse = window.matchMedia?.('(pointer: coarse)').matches;
  return isIOS || coarse ? 1 : 2;
}

function canvasToObjectUrl(canvas: HTMLCanvasElement): Promise<string> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('canvas.toBlob returned null'));
          return;
        }
        resolve(URL.createObjectURL(blob));
      },
      'image/png',
    );
  });
}

/**
 * "Share" tab for the Price History dialog. Shows live-filled design previews
 * (scaled down); tapping one mounts that design at full 1080x1080 off-screen,
 * rasterises it with html2canvas, and offers download / native share.
 *
 * Grid previews use thumb JPEGs for backgrounds so opening the tab does not
 * decode six multi‑MB photos on iOS Safari.
 */
const GRID_GAP = 16; // theme.spacing(2)

const PricePromoShareTab: React.FC<PricePromoShareTabProps> = ({ values, fileName }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Width of one cell in the 2-column grid, measured so the fixed-size (1080px)
  // template markup can be transform-scaled to fit each square preview.
  const [cellWidth, setCellWidth] = useState(0);

  const gridRef = useRef<HTMLDivElement>(null);
  const captureRef = useRef<HTMLDivElement>(null);
  // Live blob: URL for the generated PNG — revoked on replace / close / unmount.
  const imageUrlRef = useRef<string | null>(null);

  const previewScale = cellWidth / PRICE_PROMO_CANVAS;

  const selectedTemplate = PRICE_PROMO_TEMPLATES.find((t) => t.id === selectedId) ?? null;

  const previewHtmlById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const template of PRICE_PROMO_TEMPLATES) {
      map[template.id] = fillPromoTemplatePreview(template.markup, values, template.id);
    }
    return map;
  }, [values]);

  const clearImageUrl = useCallback(() => {
    if (imageUrlRef.current) {
      URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = null;
    }
    setImageUrl(null);
  }, []);

  const handleClosePreview = useCallback(() => {
    setSelectedId(null);
    clearImageUrl();
    setError(null);
    setGenerating(false);
  }, [clearImageUrl]);

  useEffect(() => () => clearImageUrl(), [clearImageUrl]);

  // Track the grid width so each preview can scale to fill its (square) cell,
  // keeping a clean 2-up layout across dialog / orientation sizes. Measured in a
  // layout effect so the first paint already has the right scale (no flicker).
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const measure = () => setCellWidth(Math.max(0, (el.clientWidth - GRID_GAP) / 2));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Render the selected design off-screen and rasterise it once its background
  // photo and the web fonts are ready.
  useEffect(() => {
    if (!selectedTemplate) return;
    let cancelled = false;

    setGenerating(true);
    clearImageUrl();
    setError(null);

    (async () => {
      try {
        const el = captureRef.current;
        if (!el) return;
        await waitForImages(el);
        if (document.fonts?.ready) {
          try {
            await document.fonts.ready;
          } catch {
            /* fonts.ready can reject on some browsers — capture anyway */
          }
        }
        // Give the browser one frame to settle layout before capture.
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        if (cancelled) return;

        const canvas = await html2canvas(el, {
          useCORS: true,
          // Mobile: 1× (1080) to avoid iOS Safari OOM; desktop keeps 2× for sharper previews.
          scale: captureScale(),
          width: PRICE_PROMO_CANVAS,
          height: PRICE_PROMO_CANVAS,
          backgroundColor: null,
          logging: false,
        });
        if (cancelled) return;

        // Blob object URLs avoid the large base64 string from toDataURL, which
        // frequently tips iOS Safari over its per-tab memory limit.
        const url = await canvasToObjectUrl(canvas);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        imageUrlRef.current = url;
        setImageUrl(url);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to generate price-promo image:', err);
          setError('Could not generate the image. Please try again.');
        }
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Capture once per template selection. `values` is read from the closure and
    // is stable while this modal preview is open, so it is intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, clearImageUrl]);

  const handleDownload = useCallback(() => {
    if (!imageUrl) return;
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = `${fileName}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [imageUrl, fileName]);

  const handleNativeShare = useCallback(async () => {
    if (!imageUrl) return;
    try {
      const blob = await (await fetch(imageUrl)).blob();
      const file = new File([blob], `${fileName}.png`, { type: 'image/png' });
      const nav = navigator as Navigator & {
        canShare?: (data?: ShareData) => boolean;
      };
      if (nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: 'Price Promo' });
      } else {
        handleDownload();
      }
    } catch (err) {
      // AbortError = user dismissed the share sheet; ignore.
      if ((err as Error)?.name !== 'AbortError') {
        console.error('Native share failed, falling back to download:', err);
        handleDownload();
      }
    }
  }, [imageUrl, fileName, handleDownload]);

  const canNativeShare =
    typeof navigator !== 'undefined' &&
    typeof (navigator as Navigator & { canShare?: unknown }).canShare === 'function';

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2, color: 'var(--color-text-secondary, rgba(255,255,255,0.7))' }}>
        Choose a design to generate a shareable price-promo image.
      </Typography>

      <Box
        ref={gridRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: `${GRID_GAP}px`,
        }}
      >
        {PRICE_PROMO_TEMPLATES.map((template) => (
          <Box key={template.id} sx={{ minWidth: 0 }}>
            <Box
              onClick={() => setSelectedId(template.id)}
              sx={{
                width: '100%',
                height: cellWidth || undefined,
                aspectRatio: '1 / 1',
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                border: '1px solid rgba(var(--color-text-rgb, 255,255,255), 0.15)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                bgcolor: '#123037',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                },
              }}
            >
              {previewScale > 0 && (
                <Box
                  sx={{
                    width: PRICE_PROMO_CANVAS,
                    height: PRICE_PROMO_CANVAS,
                    transform: `scale(${previewScale})`,
                    transformOrigin: 'top left',
                    pointerEvents: 'none',
                  }}
                  dangerouslySetInnerHTML={{ __html: previewHtmlById[template.id] }}
                />
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Off-screen full-size render used as the html2canvas capture source. */}
      {selectedTemplate && (
        <Box
          aria-hidden
          sx={{
            position: 'fixed',
            top: 0,
            left: -100000,
            width: PRICE_PROMO_CANVAS,
            height: PRICE_PROMO_CANVAS,
            pointerEvents: 'none',
          }}
        >
          <div
            ref={captureRef}
            style={{ width: PRICE_PROMO_CANVAS, height: PRICE_PROMO_CANVAS }}
            dangerouslySetInnerHTML={{ __html: fillPromoTemplate(selectedTemplate.markup, values) }}
          />
        </Box>
      )}

      {/* Preview + download dialog */}
      <Dialog open={Boolean(selectedTemplate)} onClose={handleClosePreview} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <IconButton onClick={handleClosePreview} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1 / 1',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.2)',
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={selectedTemplate?.name}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : error ? (
              <Typography variant="body2" color="error" sx={{ p: 3, textAlign: 'center' }}>
                {error}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress />
                <Typography variant="caption" color="text.secondary">
                  Generating image…
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
          {canNativeShare && (
            <Button
              onClick={handleNativeShare}
              disabled={!imageUrl || generating}
              startIcon={<ShareIcon />}
            >
              Share
            </Button>
          )}
          <Button
            onClick={handleDownload}
            disabled={!imageUrl || generating}
            variant="contained"
            startIcon={<DownloadIcon />}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricePromoShareTab;
